/**
 * What a downloaded file is named, in ONE place.
 *
 * Before this, every download surface derived its own filename and they disagreed. The bulk ZIP
 * named a studio deliverable `<SKU>_<n>.png` (resolved server-side, because the client did not
 * know the SKU); the single download on the same card wrote `4a2e55191e6443069f68a2c97abb5465.png`
 * — the CDN object name — because it never reaches the backend at all: it is a
 * `fetch` -> `blob` -> `<a download>` click, and the only thing it posts is a fire-and-forget
 * analytics ping. Same image, same page, two different names, one of them meaningless to whoever
 * receives the file.
 *
 * So the rule lives here and both paths call it. `useDownloadLogic` used to spell
 * `base || getFilenameFromUrl(url, ext)` out at four separate branches, which is why adding a
 * naming rule meant touching five places.
 *
 * `getFilenameFromUrl` and `sanitizeFilename` moved here from `./utils` and are re-exported from
 * it, so the 22 files importing them are untouched. Same reason `./platform` exists: `utils.ts`
 * cannot be imported by a unit test, because `pingDownloadLog` pulls the HttpClient/Redux/lottie
 * tree in with it — and naming is exactly the thing that wants unit tests.
 */

export const getFilenameFromUrl = (url: string, fileType: string): string => {
  const match = url.match(/\/([^/?#]+)[^/]*$/);
  if (match) {
    return match[1];
  }

  return `image.${fileType}`;
};

/**
 * Sanitize a user-typed download base name (no extension). Strips path
 * separators and control chars, trims leading/trailing dots/whitespace, and
 * caps the length. Returns "" when nothing valid remains, so callers can fall
 * back to the URL-derived default.
 */
export const sanitizeFilename = (name: string): string => {
  if (!name) {
    return "";
  }

  // Strip path separators (/, \) and control chars; keep everything else (incl. Unicode, spaces).
  // eslint-disable-next-line no-control-regex
  const stripped = name.replace(/[/\\\x00-\x1f\x7f]/g, "");
  // Trim leading/trailing dots + whitespace, cap length, then re-trim in case the
  // cap left a trailing dot/space at the new boundary.
  const trimmed = stripped.replace(/^[.\s]+|[.\s]+$/g, "").slice(0, 100);

  return trimmed.replace(/^[.\s]+|[.\s]+$/g, "");
};

/** Which product a render belongs to, as far as naming is concerned. */
export interface DownloadSubject {
  /** The product's SKU — `uploaded_files.external_id`, carried on the studio result. */
  sku?: string | null;
  /**
   * The render's own 1-based slot within that SKU's set, from `delivery_order + 1`.
   *
   * It comes off the RENDER rather than from its position in a list, because every positional
   * number we could compute here moves: an added edit shifts the ones after it, a deleted render
   * renumbers the rest, and the ZIP's own counter depends on how many files the user happened to
   * ROLL into that archive. A filename that has already gone to a customer must not later belong
   * to a different image, so the number has to be a property of the render, not of its
   * neighbours.
   */
  slot?: number | null;
}

/**
 * The SKU as a filename segment.
 *
 * Deliberately STRICTER than `sanitizeFilename` (which sanitizes a name the user typed into the
 * download menu, and keeps `<>:"|?*` because it always has): a SKU is external-system text that
 * we splice in without the user seeing it first, and it has to survive extraction on Windows.
 * The rules mirror the backend's `_sanitize_zip_name(sku, 60)` so a single download and a ZIP
 * entry cannot end up with two spellings of the same SKU.
 *
 * One accepted divergence from the backend: Python strips every character in Unicode category
 * `C`, and matching that here would need `\p{C}`, which needs a target of ES2018 (this app is
 * `es5`). Control characters are covered explicitly; a format character such as a zero-width
 * space would survive here and not there. It is invisible in a filename either way, and no SKU
 * in production contains one.
 *
 * Returns "" when nothing usable is left, so a SKU of `///` falls back to the default name
 * instead of producing `_1.png`.
 */
export const sanitizeSkuSegment = (raw: string | null | undefined): string => {
  if (!raw) {
    return "";
  }

  const normalized = typeof String.prototype.normalize === "function" ? raw.normalize("NFC") : raw;
  // Path separators and control chars, then the chars that break ZIP extraction on Windows and
  // the Content-Disposition header.
  // eslint-disable-next-line no-control-regex
  const stripped = normalized.replace(/[/\\\x00-\x1f\x7f<>:"|?*]/g, "");
  const trimmed = stripped.replace(/^[.\s]+|[.\s]+$/g, "").slice(0, 60);

  return trimmed.replace(/^[.\s]+|[.\s]+$/g, "");
};

/**
 * The filename for one download, extension included.
 *
 * Precedence, and each step is load-bearing:
 *
 * 1. **What the user typed** in the download menu. An explicit name always wins — they are
 *    looking at the field.
 * 2. **`<SKU>_<slot>`**, when the product has a SKU *and* the render has a slot. Both halves are
 *    required: a slot with no SKU would be `_3.png` (a number naming nothing), and a SKU with no
 *    slot would be the same `<SKU>.png` for every render of that product — which in an archive
 *    means the entries collide and the ZIP silently keeps the last one.
 * 3. **The CDN object name**, which is what every download does today and what every surface
 *    with no product behind it (design canvas, tech pack artwork) keeps.
 *
 * Step 2 is therefore forward-looking on purpose: renders created before slots were written have
 * none, so they land on step 3 rather than on a number we invented for them at read time.
 */
export const resolveDownloadName = (url: string, extension: string, userTyped?: string | null, subject?: DownloadSubject | null): string => {
  const typed = sanitizeFilename(userTyped ?? "");
  if (typed) {
    return `${typed}.${extension}`;
  }

  const sku = sanitizeSkuSegment(subject?.sku);
  const slot = subject?.slot;
  if (sku && typeof slot === "number" && Number.isFinite(slot) && slot > 0) {
    return `${sku}_${slot}.${extension}`;
  }

  // `getFilenameFromUrl` keeps the URL's own extension, so strip it before re-appending the
  // requested one — otherwise "Download as JPG" on a PNG hands back `x.png.jpg`.
  const fromUrl = getFilenameFromUrl(url, extension).replace(/\.[^/.]+$/, "");

  return `${fromUrl}.${extension}`;
};

/**
 * The base name (no extension) for callers that append their own — `downloadImageInFormat` takes
 * a base and derives the extension from the format it re-encodes to.
 *
 * Returns "" when the answer would be the URL-derived default, because that is what those callers
 * already treat as "you decide": passing the URL name back to them would work, but it would also
 * silence the one difference between "no name" and "this exact name".
 */
export const resolveDownloadBaseName = (userTyped?: string | null, subject?: DownloadSubject | null): string => {
  const typed = sanitizeFilename(userTyped ?? "");
  if (typed) {
    return typed;
  }

  const sku = sanitizeSkuSegment(subject?.sku);
  const slot = subject?.slot;
  if (sku && typeof slot === "number" && Number.isFinite(slot) && slot > 0) {
    return `${sku}_${slot}`;
  }

  return "";
};

/**
 * Whether a design's image already IS an SVG (a `.svg` file, by its path — a signed query does not hide it — or an
 * inline `data:image/svg+xml`). The design agent puts SVGs it converted on the canvas (2026-10-22): "Download as SVG"
 * for one of those is the file itself, not another conversion (~80 s, and credits).
 */
export const isSvgUrl = (url: string | null | undefined): boolean => {
  if (!url) return false;
  if (/^data:image\/svg\+xml/i.test(url)) return true;
  try {
    return new URL(url, "https://x.invalid").pathname.toLowerCase().endsWith(".svg");
  } catch {
    return /\.svg(\?|#|$)/i.test(url);
  }
};
